import { useEffect, useMemo, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { Loader2, RotateCcw, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  TrainingInitialMessage,
  TrainingPlaygroundTranscriptMessage,
  useGenerateTrainingTeenPlaygroundReply,
} from "@/lib/api/training-simulator-content";
import { cn } from "@/lib/utils";

const toTranscript = (
  messages: TrainingInitialMessage[],
): TrainingPlaygroundTranscriptMessage[] =>
  messages.map((message) => ({
    sender:
      message.sender_type === "mentor" ? "mentor" : "simulated_teen",
    content: message.content,
  }));

interface StoredPlaygroundState {
  transcript: TrainingPlaygroundTranscriptMessage[];
  draft: string;
}

const createInitialState = (
  transcript: TrainingPlaygroundTranscriptMessage[],
): StoredPlaygroundState => ({
  transcript,
  draft: "",
});

const isTranscriptMessage = (
  value: unknown,
): value is TrainingPlaygroundTranscriptMessage => {
  if (typeof value !== "object" || value === null) return false;

  const message = value as Record<string, unknown>;
  return (
    (message.sender === "mentor" || message.sender === "simulated_teen") &&
    typeof message.content === "string"
  );
};

const loadStoredState = (storageKey: string): StoredPlaygroundState | null => {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(storageKey);
  } catch {
    return null;
  }

  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredPlaygroundState>;
    if (
      Array.isArray(parsed.transcript) &&
      parsed.transcript.every(isTranscriptMessage) &&
      typeof parsed.draft === "string"
    ) {
      return {
        transcript: parsed.transcript,
        draft: parsed.draft,
      };
    }
  } catch {
    removeStoredState(storageKey);
    return null;
  }

  removeStoredState(storageKey);
  return null;
};

const removeStoredState = (storageKey: string) => {
  try {
    window.sessionStorage.removeItem(storageKey);
  } catch {
    // Ignore unavailable storage; the playground still works without persistence.
  }
};

const saveStoredState = (
  storageKey: string,
  state: StoredPlaygroundState,
) => {
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(state));
  } catch {
    removeStoredState(storageKey);
  }
};

const loadState = (
  storageKey: string,
  initialTranscript: TrainingPlaygroundTranscriptMessage[],
): StoredPlaygroundState =>
  loadStoredState(storageKey) ?? createInitialState(initialTranscript);

const transcriptsEqual = (
  first: TrainingPlaygroundTranscriptMessage[],
  second: TrainingPlaygroundTranscriptMessage[],
) =>
  first.length === second.length &&
  first.every(
    (message, index) =>
      message.sender === second[index]?.sender &&
      message.content === second[index]?.content,
  );

interface TrainingTeenPlaygroundProps {
  teenId: string;
  isDirty: boolean;
  initialMessages: TrainingInitialMessage[];
}

export function TrainingTeenPlayground({
  teenId,
  isDirty,
  initialMessages,
}: TrainingTeenPlaygroundProps) {
  const storageKey = `training-teen-playground:v1:${teenId}`;
  const initialTranscript = useMemo(
    () => toTranscript(initialMessages),
    [initialMessages],
  );
  const [playgroundState, setPlaygroundState] = useState(() =>
    loadState(storageKey, initialTranscript),
  );
  const generateReply = useGenerateTrainingTeenPlaygroundReply();
  const canSend =
    !isDirty &&
    !generateReply.isPending &&
    playgroundState.draft.trim().length > 0;

  useEffect(() => {
    setPlaygroundState(loadState(storageKey, initialTranscript));
  }, [initialTranscript, storageKey]);

  useEffect(() => {
    if (
      !playgroundState.draft &&
      transcriptsEqual(playgroundState.transcript, initialTranscript)
    ) {
      removeStoredState(storageKey);
      return;
    }

    saveStoredState(storageKey, playgroundState);
  }, [initialTranscript, playgroundState, storageKey]);

  const reset = () => {
    setPlaygroundState(createInitialState(initialTranscript));
    removeStoredState(storageKey);
  };

  const send = async () => {
    const content = playgroundState.draft.trim();
    if (!content || !canSend) return;

    const nextTranscript: TrainingPlaygroundTranscriptMessage[] = [
      ...playgroundState.transcript,
      { sender: "mentor", content },
    ];
    setPlaygroundState({ transcript: nextTranscript, draft: "" });

    try {
      const result = await generateReply.mutateAsync({
        teenId,
        transcript: nextTranscript.slice(-30),
      });
      setPlaygroundState({
        transcript: [...nextTranscript, result.reply],
        draft: "",
      });
    } catch {
      setPlaygroundState(playgroundState);
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between space-y-0 p-4 pb-3">
        <div className="space-y-1">
          <CardTitle className="text-base">Prompt Playground</CardTitle>
          <CardDescription className="text-xs">
            Test the saved persona without starting a training attempt.
          </CardDescription>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={reset}
          disabled={generateReply.isPending}
        >
          <RotateCcw className="mr-1 h-4 w-4" />
          Reset
        </Button>
      </CardHeader>
      <CardContent className="space-y-3 p-4 pt-0">
        {isDirty && (
          <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
            Save changes before testing them.
          </div>
        )}

        <div className="min-h-[420px] max-h-[calc(100vh-320px)] space-y-2 overflow-y-auto rounded-md border border-gray-100 bg-gray-50 p-3">
          {playgroundState.transcript.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">
              No messages yet.
            </p>
          ) : (
            playgroundState.transcript.map((message, index) => {
              const isMentor = message.sender === "mentor";
              return (
                <div
                  key={`${message.sender}-${index}`}
                  className={cn(
                    "flex",
                    isMentor ? "justify-end" : "justify-start",
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[80%] rounded-lg px-3 py-2 text-sm",
                      isMentor
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-800 ring-1 ring-gray-200",
                    )}
                  >
                    {message.content}
                  </div>
                </div>
              );
            })
          )}
          {generateReply.isPending && (
            <div className="flex justify-start">
              <div className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm text-gray-500 ring-1 ring-gray-200">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Replying
              </div>
            </div>
          )}
        </div>

        <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_auto]">
          <TextareaAutosize
            className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:bg-gray-50"
            minRows={2}
            placeholder="Type a mentor message..."
            value={playgroundState.draft}
            onChange={(event) =>
              setPlaygroundState((current) => ({
                ...current,
                draft: event.target.value,
              }))
            }
            onKeyDown={(event) => {
              if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                void send();
              }
            }}
            disabled={isDirty || generateReply.isPending}
          />
          <Button
            type="button"
            onClick={() => void send()}
            disabled={!canSend}
            className="self-end"
          >
            {generateReply.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-1 h-4 w-4" />
            )}
            Send
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
